/**
 * ============================================================================
 * DASHBOARD METRICS — FORECAST (single source of truth)
 * ============================================================================
 *
 * This module is the ONE place the owner/CEO-dashboard forecast math lives.
 * Both the metric-card FACE (via `useCeoForecastMetrics`) and every DRILLDOWN
 * modal (`MetricDrilldownModal`) compute their numbers from the functions here,
 * so a card's headline value and the sum of its drilldown rows are identical by
 * construction — they cannot drift.
 *
 * WHY THIS EXISTS: previously the face hook and the drilldown each re-derived
 * the math with different filters/defaults (e.g. the drilldown imputed a 50%
 * probability the face excluded), so "$45,000 on the card" didn't match the sum
 * behind it. Consolidating the math here removes that whole class of bug AND
 * makes future definition changes a one-file edit.
 *
 * DEFINITIONS (confirmed with ownership 2026-06):
 *
 *  • Weighted Pipeline — a projection of where the quarter / selected 90-day
 *    window lands. The whole dashboard is date-filtered (next 90 days or a past
 *    quarter) via the top-right period picker. Within that window:
 *      - WON deals count at 100% of revenue (the work is already done/committed).
 *      - ACTIVE sales-pipeline deals count at revenue × close_probability.
 *      - An active deal with no/invalid probability contributes $0 but is
 *        surfaced as "needs a probability" (never silently imputed).
 *      - LOST deals are excluded (and aren't fetched).
 *    Intent: tell the owner "go win NEW deals" vs "close what we already have."
 *
 *  • Committed Revenue — full revenue of deals in Contract Signed / Negotiation.
 *
 *  • Recurring revenue recognition (owner decision, June 2026 — multi-year
 *    contract methodology): a recurring deal's contribution to the weighted
 *    pipeline is only its FIRST 3 MONTHS of MRR (3 × monthly_amount), NOT the
 *    full multi-year TCV. A 48-month $38,400 contract therefore adds ~$2,400 to
 *    the quarter's projection, not $38,400 — so one long contract can't swamp a
 *    quarter's hunting signal. A one-time deal contributes its full revenue.
 *
 *  • Recurring MRR card — monthly_amount of WON recurring deals (the monthly
 *    run-rate). Distinct from the recognized pipeline amount above.
 * ============================================================================
 */

import {
  addCalendarMonths,
  businessDateParts,
  businessDateString,
  zonedWallClockToUtc,
} from "@west-tennessee-consulting/business-date";

import { parseBigFloat, type BigFloatValue } from "@west-tennessee-consulting/ui/lib/currency";
import type { Deal, DealStage } from "@/lib/types/database/deals";
import {
  hasDealStage,
  isActiveStage,
  isLostStage,
  isWonDeal,
} from "@/lib/constants/deal-stages";

// ----------------------------------------------------------------------------
// What the forecast math actually READS
// ----------------------------------------------------------------------------
//
// `Deal` is the CLIENT-side view of a deal row: it declares nullable columns as
// optional (`close_date?: string`) because that is how they arrive through
// `@wtc/ui`'s untyped browser client. A typed PostgREST read gives the same
// columns as `string | null`, so a route handler holding real rows cannot pass
// them to a `Pick<Deal, …>` parameter — and the only way to make it compile
// would be `as unknown as Deal[]`, a cast that keeps compiling however wrong
// the fields become.
//
// So the per-deal functions take these structural inputs instead. `Deal`
// satisfies them, and so does a typed `deals` row. Nothing is loosened in
// practice: every field below already went through `parseBigFloat`, which is
// what a BigFloat column needs anyway (Postgres `numeric` arrives as a STRING
// through pg_graphql).
//
// Added when the owner glance put these same numbers on a phone
// (`GET /api/workspace-addon/owner-glance`) — one formula, two surfaces, and
// `tsc` checking both for real.

/** The fields every forecast figure is derived from. */
export interface ForecastDealInput {
  /**
   * Nullable, because the column is. A stage-less row is in NO bucket — not
   * won, not active, not lost — which matches what Postgres already does to it
   * (`stage NOT IN (…lost…)` drops a NULL, so the forecast query never returns
   * one; the sales-performance query has no stage filter and can).
   *
   * `undefined` is admitted alongside `null` because a codegen row types a
   * nullable column as `?: Maybe<T>` — pg_graphql always sends the key, but a
   * partially-constructed row (an RPC projection, a form draft) may not carry
   * it. Every consumer below already tested `=== undefined`; only the declared
   * type disagreed.
   */
  stage?: DealStage | null;
  revenue?: BigFloatValue;
  close_probability?: BigFloatValue;
  is_recurring?: boolean | null;
  monthly_amount?: BigFloatValue;
  contract_term_months?: BigFloatValue;
  /** The true contract start for a won recurring deal. */
  won_at?: string | null;
  /** The renewal anchor's fallback, and the period bucket. */
  close_date?: string | null;
}

/** Stages whose full revenue is treated as "committed". */
export const COMMITTED_REVENUE_STAGES: DealStage[] = [
  "contract_signed",
  "negotiation",
];

/** Months of a recurring deal's MRR recognized in the weighted pipeline at logging time. */
export const RECURRING_RECOGNITION_MONTHS = 3;

/** Default contract length (months) applied when a deal is set recurring. */
export const DEFAULT_CONTRACT_TERM_MONTHS = 48;

export function isCommittedRevenueStage(stage: DealStage): boolean {
  return COMMITTED_REVENUE_STAGES.includes(stage);
}

/** Probability (0–100) a deal contributes at, by stage. Won = certain (100). */
export function weightedProbabilityPercent(
  deal: Pick<ForecastDealInput, "stage" | "close_probability">,
): number | null {
  const stage = deal.stage;
  if (stage === null || stage === undefined) return null;
  if (isWonDeal(stage)) return 100;
  if (!isActiveStage(stage)) return null;
  const prob = parseBigFloat(deal.close_probability);
  if (prob === null || prob < 0 || prob > 100) return null;
  return prob;
}

/**
 * Non-negative revenue for a deal, parsed safely from BigFloat (string|number).
 * Invalid / negative → 0 (never NaN, never string-concatenated).
 */
export function dealRevenue(deal: Pick<ForecastDealInput, "revenue">): number {
  const value = parseBigFloat(deal.revenue);
  return value !== null && value >= 0 ? value : 0;
}

/** Monthly recurring amount for a deal, parsed safely. */
export function dealMonthlyAmount(deal: Pick<ForecastDealInput, "monthly_amount">): number {
  const value = parseBigFloat(deal.monthly_amount);
  return value !== null && value >= 0 ? value : 0;
}

/** True when a deal belongs in the weighted-pipeline view (won or active, not lost). */
export function isInWeightedPipeline(deal: Pick<ForecastDealInput, "stage">): boolean {
  const stage = deal.stage;
  if (stage === null || stage === undefined) return false;
  if (isLostStage(stage)) return false;
  return isWonDeal(stage) || isActiveStage(stage);
}

/**
 * Revenue RECOGNIZED for a deal in the weighted pipeline (the projection basis).
 *
 * - One-time deal → full TCV (`revenue`).
 * - Recurring deal → first `RECURRING_RECOGNITION_MONTHS` (3) months of MRR
 *   (`monthly_amount`), capped at the contract term so a < 3-month term isn't
 *   over-recognized. This is the owner-decided rule that keeps a multi-year
 *   contract from dumping its whole TCV into one quarter.
 */
export function dealRecognizedRevenue(
  deal: Pick<ForecastDealInput, "revenue" | "is_recurring" | "monthly_amount" | "contract_term_months">,
): number {
  if (deal.is_recurring) {
    const monthly = dealMonthlyAmount(deal);
    if (monthly <= 0) return 0;
    const term = parseBigFloat(deal.contract_term_months) ?? DEFAULT_CONTRACT_TERM_MONTHS;
    const months = Math.min(RECURRING_RECOGNITION_MONTHS, term > 0 ? term : RECURRING_RECOGNITION_MONTHS);
    return monthly * months;
  }
  return dealRevenue(deal);
}

/** True when a deal counts toward Recurring MRR (won + recurring + has a monthly amount). */
export function isRecurringMrrDeal(
  deal: Pick<ForecastDealInput, "stage" | "is_recurring" | "monthly_amount">,
): boolean {
  const stage = deal.stage;
  if (stage === null || stage === undefined) return false;
  return isWonDeal(stage) && Boolean(deal.is_recurring) && dealMonthlyAmount(deal) > 0;
}

/**
 * Weighted contribution of a single deal to the pipeline, over its RECOGNIZED
 * revenue (full TCV for one-time deals; first 3 months of MRR for recurring).
 * Won → recognized revenue; active → recognized × probability/100; otherwise 0.
 * An active deal with no valid probability contributes 0 (see `needsProbability`).
 */
export function weightedPipelineContribution(
  deal: Pick<
    ForecastDealInput,
    "stage" | "revenue" | "close_probability" | "is_recurring" | "monthly_amount" | "contract_term_months"
  >,
): number {
  if (!isInWeightedPipeline(deal)) return 0;
  const recognized = dealRecognizedRevenue(deal);
  if (deal.stage && isWonDeal(deal.stage)) return recognized;
  const prob = weightedProbabilityPercent(deal);
  if (prob === null) return 0;
  return recognized * (prob / 100);
}

export function committedRevenueContribution(
  deal: Pick<ForecastDealInput, "stage" | "revenue">,
): number {
  return deal.stage !== null && deal.stage !== undefined && isCommittedRevenueStage(deal.stage)
    ? dealRevenue(deal)
    : 0;
}

export function recurringMrrContribution(
  deal: Pick<ForecastDealInput, "stage" | "is_recurring" | "monthly_amount">,
): number {
  return isRecurringMrrDeal(deal) ? dealMonthlyAmount(deal) : 0;
}

// ----------------------------------------------------------------------------
// Renewal risk — won recurring contracts approaching end of term
// ----------------------------------------------------------------------------
//
// No renewal/end date is stored on a deal, so it's DERIVED from the contract
// start (won_at, falling back to close_date) plus contract_term_months. "Due
// soon" = within the next RENEWAL_WINDOW_DAYS, plus a short just-lapsed grace so
// a contract that quietly rolled past term still surfaces for attention.

/** How far ahead a renewal counts as "due soon". */
export const RENEWAL_WINDOW_DAYS = 90;
/** Grace window behind today so a just-lapsed renewal still surfaces. */
export const RENEWAL_LAPSED_GRACE_DAYS = 30;
const MS_PER_DAY = 1000 * 60 * 60 * 24;

type RenewalDeal = Pick<
  ForecastDealInput,
  "stage" | "is_recurring" | "won_at" | "close_date" | "contract_term_months" | "monthly_amount"
>;

/**
 * Derived renewal/end date for a WON recurring deal, or null if it isn't one or
 * has no usable start anchor. `won_at` is the true contract start; `close_date`
 * is the fallback anchor.
 */
export function dealRenewalDate(deal: RenewalDeal): Date | null {
  const stage = deal.stage;
  if (stage === null || stage === undefined) return null;
  if (!isWonDeal(stage) || !deal.is_recurring) return null;
  const anchorStr = deal.won_at ?? deal.close_date;
  if (!anchorStr) return null;
  const anchor = new Date(anchorStr);
  if (Number.isNaN(anchor.getTime())) return null;
  const term = parseBigFloat(deal.contract_term_months);
  const months = term !== null && term > 0 ? Math.round(term) : DEFAULT_CONTRACT_TERM_MONTHS;
  // `setMonth` OVERFLOWS: a contract won on the 31st + 1 month lands in the
  // month after next, and it does its arithmetic in the runtime's zone (UTC on
  // Vercel). `addCalendarMonths` clamps to the end of the target month and does
  // it in no zone at all.
  const anchorDate = businessDateString(anchor);
  const renewalDate = addCalendarMonths(anchorDate, months);
  const { hour, minute } = businessDateParts(anchor);
  return zonedWallClockToUtc(
    Number(renewalDate.slice(0, 4)),
    Number(renewalDate.slice(5, 7)),
    Number(renewalDate.slice(8, 10)),
    hour,
    minute
  );
}

/** Whole days from `now` until the deal's derived renewal date (negative = past). */
export function renewalDaysUntil(deal: RenewalDeal, now: Date): number | null {
  const renewal = dealRenewalDate(deal);
  if (!renewal) return null;
  return Math.floor((renewal.getTime() - now.getTime()) / MS_PER_DAY);
}

/** True when a deal's renewal falls in the attention window [-grace, +window]. */
export function isRenewingSoon(deal: RenewalDeal, now: Date): boolean {
  const days = renewalDaysUntil(deal, now);
  if (days === null) return false;
  return days <= RENEWAL_WINDOW_DAYS && days >= -RENEWAL_LAPSED_GRACE_DAYS;
}

export interface RenewalsDueSoon {
  /** Number of won recurring deals renewing within the attention window. */
  count: number;
  /** Total monthly recurring revenue tied to those renewals (MRR at risk). */
  mrrAtRisk: number;
}

/** Count + MRR of won recurring deals whose derived renewal is due soon. */
export function computeRenewalsDueSoon(
  deals: readonly RenewalDeal[],
  now: Date,
): RenewalsDueSoon {
  let count = 0;
  let mrrAtRisk = 0;
  for (const deal of deals) {
    if (isRenewingSoon(deal, now)) {
      count += 1;
      mrrAtRisk += dealMonthlyAmount(deal);
    }
  }
  return { count, mrrAtRisk: roundCurrency(mrrAtRisk) };
}

/** An active pipeline deal whose probability is missing/invalid (excluded from the $). */
export function isActiveDealNeedingProbability(
  deal: Pick<ForecastDealInput, "stage" | "close_probability">,
): boolean {
  const stage = deal.stage;
  if (stage === null || stage === undefined) return false;
  return isActiveStage(stage) && weightedProbabilityPercent(deal) === null;
}

// ----------------------------------------------------------------------------
// Per-deal breakdown rows (what the drilldown modal renders + sums)
// ----------------------------------------------------------------------------

export type ForecastMetricType = "weighted-pipeline" | "committed-revenue" | "recurring-mrr";

export interface ForecastBreakdownRow {
  deal: Deal;
  /** This deal's dollar contribution to the metric — what the modal lists + sums. */
  contribution: number;
  /** Probability % shown for weighted rows (100 for won, the deal's prob otherwise). */
  probabilityPercent: number | null;
  /** Human-readable explanation of the contribution. */
  details: string;
}

function roundCurrency(value: number): number {
  return Math.round(value * 100) / 100;
}

/**
 * The deals (with their contribution) that make up a metric, sorted by
 * contribution descending. `sum(rows.contribution)` equals the metric's face
 * value because the face aggregators below sum the exact same per-deal function.
 */
export function forecastBreakdown(deals: Deal[], metricType: ForecastMetricType): ForecastBreakdownRow[] {
  const rows: ForecastBreakdownRow[] = [];

  for (const deal of deals) {
    if (metricType === "weighted-pipeline") {
      if (!isInWeightedPipeline(deal)) continue;
      const contribution = weightedPipelineContribution(deal);
      const prob = weightedProbabilityPercent(deal);
      const won = hasDealStage(deal.stage) && isWonDeal(deal.stage);
      const basis = deal.is_recurring
        ? `first ${RECURRING_RECOGNITION_MONTHS} months ($${dealMonthlyAmount(deal)}/mo recurring)`
        : `$${dealRevenue(deal)} (one-time)`;
      const details =
        !won && prob === null
          ? "No probability set — not counted (add a probability)"
          : won
            ? `${basis} at 100% (won)`
            : `${basis} × ${prob}% probability`;
      rows.push({ deal, contribution, probabilityPercent: won ? 100 : prob, details });
    } else if (metricType === "committed-revenue") {
      if (!hasDealStage(deal.stage) || !isCommittedRevenueStage(deal.stage)) continue;
      rows.push({
        deal,
        contribution: committedRevenueContribution(deal),
        probabilityPercent: null,
        details: `Full amount (${deal.stage} stage)`,
      });
    } else {
      if (!isRecurringMrrDeal(deal)) continue;
      rows.push({
        deal,
        contribution: recurringMrrContribution(deal),
        probabilityPercent: null,
        details: "Monthly recurring amount (won)",
      });
    }
  }

  return rows.sort((a, b) => b.contribution - a.contribution);
}

// ----------------------------------------------------------------------------
// Face aggregator (what the metric cards display)
// ----------------------------------------------------------------------------

export interface ForecastAggregate {
  weightedPipeline: number;
  committedRevenue: number;
  recurringMRR: number;
  /** Active deals with a valid probability (used for the confidence indicator). */
  activeDealsWithProbability: number;
  /** Active deals missing a probability — excluded from the weighted $, surfaced as a nudge. */
  activeDealsNeedingProbability: number;
  /** Won deals counted at 100% in the weighted pipeline. */
  wonDealsCounted: number;
}

/**
 * Aggregate the forecast metrics over a deal set. Each total is the sum of the
 * SAME per-deal contribution function the drilldown lists — guaranteeing the
 * face value equals the drilldown total.
 */
export function aggregateForecast(deals: readonly ForecastDealInput[]): ForecastAggregate {
  let weightedPipeline = 0;
  let committedRevenue = 0;
  let recurringMRR = 0;
  let activeDealsWithProbability = 0;
  let activeDealsNeedingProbability = 0;
  let wonDealsCounted = 0;

  for (const deal of deals) {
    weightedPipeline += weightedPipelineContribution(deal);
    committedRevenue += committedRevenueContribution(deal);
    recurringMRR += recurringMrrContribution(deal);

    const stage = deal.stage;
    if (stage === null || stage === undefined) continue;
    if (isActiveStage(stage)) {
      if (weightedProbabilityPercent(deal) === null) activeDealsNeedingProbability += 1;
      else activeDealsWithProbability += 1;
    } else if (isWonDeal(stage)) {
      wonDealsCounted += 1;
    }
  }

  return {
    weightedPipeline: roundCurrency(weightedPipeline),
    committedRevenue: roundCurrency(committedRevenue),
    recurringMRR: roundCurrency(recurringMRR),
    activeDealsWithProbability,
    activeDealsNeedingProbability,
    wonDealsCounted,
  };
}
